import { format } from "../utils/format";
import instadate from 'instadate';
import { formActions } from 'redux-pure-form';

import { DEFAULT_GROUP } from '../ui/ui.const.js';
import type { Event } from '../calendar/calendar.types.js';

import {
  ASC,
  DEFAULT_EVENT_COLOR,
  DEFAULT_FILTER,
  defaultFilterForm,
  discrepancyPriorities,
  EVENT_ADD,
  EVENT_RECURRING,
  EVENT_VIEW,
  modalsHashParams,
  statusNames,
  weekDayNames,
  MONTH,
  WEEK,
  DAY,
  DISPATCH,
  RESOURCE
} from './calendar.consts.js';

import {
  ADD_PASSENGER,
  EDIT_PASSENGER,
  WEIGHT_AND_BALANCE
} from './weight-and-balance/weight-and-balance.consts';

import { getContrastColor } from '../utils/color.utils';

export default class Calendar {
  static getYearsOptions() {
    const nowYear = new Date().getFullYear();
    const numberYears = 3;
    const yearsOptions = [];
    for (let i = nowYear - numberYears; i <= nowYear + numberYears; i++) {
      yearsOptions.push({
        label: i,
        value: i
      });
    }
    return yearsOptions;
  }
}

// ---------- PARSE INT ---------- //
export function int(string: string) {
  return parseInt(string, 10);
}

// ---------- RESOURSES UPDATE ---------- //
export function getResByEvent(
  days: $ReadOnlyArray<{ days: $ReadOnlyArray<number>, event: Event }> = [],
  { event = {}, usedResources = [] }: { event: Event | Object, usedResources: Array<any> }
) {
  return days.reduce(
    (acc, item) => ({
      ...acc,
      [item.event.id]: [
        // why is comment?
        // ...usedResources,
        ...((!item.event.resources || (item.event.resources && !item.event.resources.length)) &&
        !item.event.instructorID
          ? [
              ...(event.resources || []).map(res => res.id),
              ...(int(event.reserve) === 0 && event.instructorID ? [event.instructorID] : [])
            ]
          : []),
        ...(item.event.unmovedInResources || event.unmovedInResources || []),
        ...(item.event.resources || []).map(res => res.id),
        ...(int(item.event.reserve) === 0 && item.event.instructorID
          ? [item.event.instructorID]
          : [])
      ]
    }),
    {}
  );
}

export function getEventsByResourse(resByEvent = {}) {
  const eventsByResourse = {};

  Object.keys(resByEvent).forEach(eventID => {
    (resByEvent[eventID] || []).forEach(resID =>
      !eventsByResourse[resID]
        ? (eventsByResourse[resID] = [eventID])
        : eventsByResourse[resID].indexOf(eventID) === -1
        ? eventsByResourse[resID].push(eventID)
        : false
    );
  }, {});

  return eventsByResourse;
}

export function getNextEventsByResourse({ fromDays, toDays, ...rest }) {
  const nextResByEvent = getResByEvent(fromDays, rest);
  return {
    prevRes: getEventsByResourse(nextResByEvent),
    nextRes: getEventsByResourse(getResByEvent(toDays, rest)),
    nextResByEvent
  };
}

// ---------- TIME ---------- //
export function getTimezones(timezones) {
  return Object.keys(timezones).reduce((a, timeGroup, i) => {
    if (!i) return a; // first key is 'Recent'
    timezones[timeGroup].timeZones.forEach(zone => (a[zone.id] = zone));
    return a;
  }, {});
}

export function newDate(timestamp, offset = 0, resetTimezoneOffset = true) {
  return resetTimezoneOffset
    ? instadate.resetTimezoneOffset(
        new Date(parseInt(timestamp, 10) * 1000 + parseInt(offset, 10) * 1000)
      )
    : new Date(parseInt(timestamp, 10) * 1000 + parseInt(offset, 10) * 1000);
}

export function getTimestamp(string: string) {
  return new Date(string).getTime() / 1000;
}

export function getAccurateTimestamp(string: string) {
  return instadate.resetTimezoneOffset(new Date(string)).getTime() / 1000;
}

export function getBrowserOffset(tmst: number | string) {
  return new Date(parseInt(tmst, 10) * 1000).getTimezoneOffset() * 60;
}

export function applyTimezone(timestamp, offset = 0, revert = false) {
  return revert
    ? parseInt(timestamp, 10) - parseInt(offset, 10)
    : parseInt(timestamp, 10) + parseInt(offset, 10);
}

export function getMinutes(d: number, offset = 0, resetTimezoneOffset = true): string {
  const date = resetTimezoneOffset
    ? newDate(d, offset, true)
    : new Date(parseInt(d, 10) * 1000 + parseInt(offset, 10) * 1000);
  return date.getHours() * 60 + date.getMinutes();
}

export function parseTime(minutes, formatString) {
  const dayHalf = 12 * 60;
  const AMFormat = formatString === 'h:mm a';

  const normMinutes = minutes % 60;
  const formatMinutes = normMinutes.toString().padStart(2, '0');

  const hours = Math.floor(minutes / 60) % 24;
  const normHours = AMFormat ? (hours > 12 ? hours % 12 : hours === 0 ? 12 : hours) : hours;
  const formatHours = AMFormat ? normHours.toString().padStart(2, '0') : normHours;

  return `${formatHours}:${formatMinutes}${AMFormat ? (minutes < dayHalf ? ' AM' : ' PM') : ''}`;
}

export function parseTimeInput(input: string): number {
  const normInput = input.replace(/\s|\n|\t|:/g, ';');
  const matchedGroups = normInput.split(';');

  const matchedNoonIndex = matchedGroups.findIndex(group => /a|p/i.test(group));
  const matchedNoon = matchedGroups[matchedNoonIndex];

  const matchedHoursIndex = matchedGroups.findIndex(
    group => typeof parseInt(group.replace(/\D/g, ''), 10) === 'number'
  );
  const matchedHours =
    matchedHoursIndex > -1 && parseInt(matchedGroups[matchedHoursIndex].replace(/\D/g, ''), 10);

  const matchedMinutesIndex = matchedGroups.findIndex(
    (group, i) =>
      typeof parseInt(group.replace(/\D/g, ''), 10) === 'number' && i !== matchedHoursIndex
  );
  const matchedMinutes =
    matchedMinutesIndex > -1 && parseInt(matchedGroups[matchedMinutesIndex].replace(/\D/g, ''), 10);

  let nextHours = matchedHours ? matchedHours % 24 : 0;
  nextHours = matchedNoon ? nextHours % 12 : nextHours;
  nextHours = matchedNoon && /p/i.test(matchedNoon.toUpperCase()) ? nextHours + 12 : nextHours;

  const nextMinutes = matchedMinutes ? matchedMinutes % 60 : 0;

  return nextHours * 60 * 60 + nextMinutes * 60;
}

export function formatDate(
  date: Date | string,
  offset: number = 0,
  outputFormat: string = 'Y-m-d',
  utc: boolean = false,
  resetTimezoneOffset: boolean = true
): string {
  if (!date) return console.warn('Wrong date!');

  let normDate;
  if (utc) {
    normDate = newDate(date, 0, resetTimezoneOffset);
  } else {
    normDate = newDate(date, offset, resetTimezoneOffset);
  }
  const time = normDate.getTime();
  normDate = null; // use for perfomace reason
  return format(time, outputFormat);
}

export function isSameDayWithOffset(d1, offset1, d2, offset2) {
  return instadate.isSameDay(newDate(d1, offset1), newDate(d2, offset2));
}

// ---------- DISCREPANCY ---------- //
export function getDiscrepancyFieldValue(field, value, mechanics, list) {
  switch (field) {
    case 'assignTo':
      const selectedMechanicID = list.find(id => value === id);
      const selectedMechanic = mechanics[selectedMechanicID];
      return selectedMechanic ? `${selectedMechanic.name} ${selectedMechanic.surname}` : '—';
    case 'status':
      return statusNames[value];
    case 'priority':
      return discrepancyPriorities[value].name;
    default:
      return '—';
  }
}

// ---------- CHECKINGS ---------- //
export function isChecked(form, field, value) {
  if (form && form[field] && value) {
    return !!form[field].find(val => val.value === value);
  }
  return false;
}

export function isUnmoved(event, resourceID) {
  return event.unmovedInResources && resourceID
    ? event.unmovedInResources.indexOf(resourceID) > -1
    : false;
}

export function isOrgOverdue(orgUnique, organizationsAccess) {
  return organizationsAccess[orgUnique] ? organizationsAccess[orgUnique].overdue === '1' : false;
}

// ---------- RECURRING ---------- //
export function getRecurringSettings(
  { repsInterval, recurringType, daysOfWeek, repsType, reps, recurringEndDate, startTime },
  offset
) {
  const recurringTextRef = {
    day: {
      oneTime: 'Daily, ',
      multiTime: 'days, '
    },
    weeks: {
      oneTime: 'Weekly on ',
      multiTime: 'weeks on '
    },
    month: {
      oneTime: 'Monthly on ',
      multiTime: 'months on '
    }
  };

  let text = '';

  if (repsInterval === '1') {
    text = text.concat(recurringTextRef[recurringType].oneTime);
  } else {
    text = text.concat(`Every ${repsInterval} ${recurringTextRef[recurringType].multiTime}`);
  }

  switch (recurringType) {
    case 'weeks':
      text = text.concat(
        daysOfWeek.length === 7
          ? 'all days, '
          : daysOfWeek.filter(day => day !== '0' || day !== '6').length === 5
          ? 'weekdays, '
          : daysOfWeek
              .sort()
              .map(day => weekDayNames[day].full)
              .join(', ')
      );
      break;
    case 'month':
      text = text.concat(`day ${formatDate(startTime, offset, 'd')}, `);
      break;
  }

  if (repsType === 'number') {
    text = text.concat(` ${reps.toString().length > 0 ? reps : '0'} times`);
  } else {
    text = text.concat(` until ${formatDate(recurringEndDate, offset, 'j F, Y')}`);
  }

  return text;
}

// ---------- NORMALIZE DATA ---------- //
export function concatMessages(res, type, group) {
  const blockMessagesFields = [
    'instructorPilotsBlocker',
    'instructorsBlocker',
    'pilotsBlocker',
    'sicsBlocker'
  ];

  return blockMessagesFields
    .reduce((acc, field) => acc.concat(res.data[field] ? res.data[field] : []), [])
    .reduce((ac, text) => ac.concat({ type, group, text }), []);
}

export function extractMessages(response, group = DEFAULT_GROUP, timeout = Infinity) {
  const types = ['warning', 'message', 'info', 'error'];
  return types.reduce(
    (acc, type) => [
      ...acc,
      ...(response[type] || []).reduce((ac, text) => [...ac, { type, text, group, timeout }], [])
    ],
    []
  );
}

export function getCustomFields(list, customFields, org) {
  return list.reduce(
    (acc, fieldID) =>
      customFields[fieldID].organizationUnique === org
        ? {
            ...acc,
            [fieldID]: ''
          }
        : acc,
    {}
  );
}

export function getActivityTypesOptions(list, activityTypes, org) {
  return list.reduce((acc, activityTypeId) => {
    const activityType = activityTypes[activityTypeId];

    return activityType.organizationUnique === org
      ? acc.concat({
          value: activityType.id,
          label: activityType.name
        })
      : acc;
  }, []);
}

export function getSimpleValue(array) {
  return Object.keys(array).reduce((acum, key) => {
    const normKey = key.endsWith('s') ? key.slice(0, -1).concat('ID') : key;
    return {
      ...acum,
      [normKey]:
        Array.isArray(array[key]) && array[key].length && typeof array[key][0] === 'object'
          ? array[key]
              .reduce(
                (ac, item) => (ac.indexOf(item.value) === -1 ? ac.concat(item.value) : ac),
                []
              )
              .map(val => (key === 'instructors' || key === 'pilots' ? val.split('-')[1] : val))
          : array[key]
    };
  }, {});
}

export function capitalize(word: string): string {
  return `${word[0].toUpperCase()}${word.substring(1)}`;
}

export function getFullName(ref, key) {
  const normKey = key.split('Name')[0];
  return `
    ${ref[`${normKey}Name`]}
    ${ref[`${normKey}Middlename`] || ''}
    ${ref[`${normKey}Surname`]}
  `;
}

export function getAcceptedBy(ref, selected) {
  return Object.keys(ref).reduce((a, id) => {
    if (
      !selected ||
      !selected.length ||
      (selected.length && selected.indexOf(id.split('-')[0] > -1))
    ) {
      a[id.split('-')[1]] = ref[id];
    }
    return a;
  }, {});
}

export function getAllCheckedInGroup(groupedArr) {
  return Object.keys(groupedArr).reduce((acc, key) => {
    acc[key] = groupedArr[key].slice(1).every(opt => opt.checked);
    return acc;
  }, {});
}

export function getAllCheckedInGroupFromOptions(groupedValues, options) {
  return Object.keys(options).reduce((acc, key) => {
    acc[key] =
      options[key].slice(1).length === (groupedValues[key] ? groupedValues[key].length : false);
    return acc;
  }, {});
}

export function groupBy(arr, ref, filed) {
  return arr.reduce((accum, val) => {
    const item = ref[val.value];
    if (!item) return accum;

    if (!accum[item[filed]]) {
      accum[item[filed]] = [val];
    } else {
      accum[item[filed]] = accum[item[filed]].concat(val);
    }

    return accum;
  }, {});
}

export function joinValues(arg = '', split = false) {
  if (typeof arg === 'string') {
    return arg;
  }

  if (typeof arg === 'number') {
    return String(arg);
  }

  if (!Array.isArray(arg)) {
    return '';
  }

  const values = new Set(
    arg.map(item =>
      typeof item === 'object' ? (split ? item.value.split('-')[1] : item.value) : item
    )
  );

  return [...values].join();
}

// ---------- UTILS ---------- //
export function compareObjects(o1, o2) {
  let isEqual = true;
  Object.keys(o1).forEach(
    k =>
      (isEqual =
        isEqual &&
        (o1[k] === o2[k] ||
          ((!o1[k] || (o1[k] && !o1[k].length)) && (!o2[k] || (o2[k] && !o2[k].length)))))
  );
  return isEqual;
}

// ---------- COMPARE EVENTS ---------- //
export function compareResources(eventRes, formRes) {
  return compareObjects(eventRes.map(r => r.id), formRes);
}

// ---------- SORT ---------- //
export const makeSort = (obj, field: string, dir: string) => (a, b) => {
  if (obj[a][field] < obj[b][field]) {
    return dir === ASC ? -1 : 1;
  }
  if (obj[a][field] > obj[b][field]) {
    return dir === ASC ? 1 : -1;
  }
  return 0;
};

// ---------- URL ---------- //
export function getHash(params) {
  const nextHash = Object.keys(params)
    .reduce((accu, param) => accu.concat(`${param}=${params[param]}`), [])
    .join('&');
  return nextHash.length ? `#?${nextHash}` : '';
}

const getFilterParam = (dynamic, saved, split = false) => {
  const dynamicValue = joinValues(dynamic, split);

  const isEqual = joinValues(saved, split) === dynamicValue;

  return isEqual ? '' : dynamicValue;
};

export const isFilterModified = (filterForm, savedFilter, URL) => {
  const isAppliedFilter = filterForm.id;

  const paramsString = getParamsString(
    filterForm,
    isAppliedFilter
      ? savedFilter
      : { filter: { ...DEFAULT_FILTER, ...URL.params, organizationID: URL.params.organizations } }
  );

  return isAppliedFilter ? !/^filter=\d+;$/.test(paramsString) : paramsString.length;
};

export const isActivityTypeColorSchemeApplied = (savedFilter, URL, activeActivityTypesList) => {
  if (!activeActivityTypesList.length) {
    return false;
  }

  if (URL && URL.params && URL.params.activityTypeColorScheme) {
    return URL.params.activityTypeColorScheme === '1';
  }

  return savedFilter && savedFilter.filter && savedFilter.filter.activityTypeColorScheme === '1';
};

export const normalizeFilters = userFilters => {
  return Object.values(userFilters).reduce(
    (acc, item) => ({
      ...acc,
      [item.id]: {
        ...item,
        filter: {
          ...DEFAULT_FILTER,
          ...item.filter
        }
      }
    }),
    {}
  );
};

export function getParamsString(filterForm, savedFilter) {
  const savedFilterParams = (savedFilter && savedFilter.filter) || {};

  const organizations = getFilterParam(filterForm.organizations, savedFilterParams.organizationID);
  const locations = getFilterParam(filterForm.locations, savedFilterParams.locations);
  const resources = getFilterParam(filterForm.resources, savedFilterParams.resources);
  const instructors = getFilterParam(filterForm.instructors, savedFilterParams.instructors, true);
  const maintenance = getFilterParam(filterForm.maintenance, savedFilterParams.maintenance);
  const waiting = getFilterParam(filterForm.waiting, savedFilterParams.waiting);
  const myonly = getFilterParam(filterForm.myonly, savedFilterParams.myonly);
  const activityTypeColorScheme = getFilterParam(
    filterForm.activityTypeColorScheme,
    savedFilterParams.activityTypeColorScheme
  );
  const pilots = getFilterParam(filterForm.pilots, savedFilterParams.pilots, true);

  const filterId = typeof filterForm.id === 'string' ? filterForm.id : '';
  return `
      ${filterId.length ? `filter=${filterId};` : ''}
      ${organizations.length ? `organizations=${organizations};` : ''}
      ${locations.length ? `locations=${locations};` : ''}
      ${resources.length ? `resources=${resources};` : ''}
      ${instructors.length ? `instructors=${instructors};` : ''}
      ${maintenance.length ? `maintenance=${maintenance};` : ''}
      ${waiting.length ? `waiting=${waiting};` : ''}
      ${myonly.length ? `myonly=${myonly};` : ''}
      ${activityTypeColorScheme.length ? `activityTypeColorScheme=${activityTypeColorScheme};` : ''}
      ${pilots.length ? `pilots=${pilots};` : ''}
    `.replace(/\s|\n|\t/g, '');
}

export function getURL(URL, filterForm, savedFilter, action: string = '') {
  const paramsString = getParamsString(filterForm, savedFilter);

  return `
    ${URL.org}/
    ${URL.model}/
    ${action.length ? action : URL.action}/
    ${URL.element}/ 
    ${filterForm && filterForm.timezone ? filterForm.timezone + '/' : ''}
    ${paramsString.length ? `params:${paramsString}` : ''}
  `
    .replace(/\s|\n|\t/g, '')
    .concat('/');
}

export function parseUrlParams() {
  const hash = window.location.hash.slice(2); // '#?'
  const regex = /([^?=&]+)(=([^&]*))/g;
  const matchedGroups = hash.match(regex);
  return matchedGroups
    ? matchedGroups.reduce(
        (ac, group) => ({ ...ac, [group.split('=')[0]]: group.split('=')[1] }),
        {}
      )
    : {};
}

export function getParamsForEventAction(
  action: string,
  params,
  aliases = {
    org: 'organizationUnique',
    location: 'locationID',
    event: 'id'
  }
) {
  return modalsHashParams[action].reduce(
    (accumu, p) => {
      const param = aliases[p] || p;
      const field = params[param] || params[p];

      if (field !== null && field !== undefined) {
        return { ...accumu, [p]: field };
      }

      return accumu;
    },
    { action }
  );
}

export function parseUrlBreadcrumbs() {
  const params = parseUrlParams();
  if (params.action) {
    if (params.action !== EVENT_ADD && params.action !== EVENT_VIEW) {
      const initialAction = params.action !== EVENT_RECURRING ? EVENT_VIEW : EVENT_ADD;

      // TODO: design mechanism to build breadcrumbs chains
      if (params.action === ADD_PASSENGER || params.action === EDIT_PASSENGER) {
        return [
          getParamsForEventAction(initialAction, params),
          getParamsForEventAction(WEIGHT_AND_BALANCE, params),
          params
        ];
      }

      return [getParamsForEventAction(initialAction, params), params];
    }
    return [getParamsForEventAction(params.action, params)];
  }
  return [];
}

export function paramsToHash(params, scrollPosition) {
  window.location.hash = getHash(params);
  window.scrollTo(0, scrollPosition);
  return formActions.onChange('URL.eventParams', params);
}

export function changeView(action, URL, filterForm, savedFilter, reportType, pilotID) {
  let formatStr = 'Y-m-d';
  if (action === MONTH) formatStr = 'Y-m';
  if (action === WEEK) formatStr = 'Y-W';

  let date = URL.element;
  if (URL.action !== DAY && window.sessionStorage) {
    date = window.sessionStorage.getItem('selectedDate') || URL.element;
  }
  let element = formatDate(
    int(URL.eventParams.finishTime) + 86400 || // day
      instadate.resetTimezoneOffset(new Date(date)).getTime() / 1000,
    0,
    formatStr,
    false,
    false
  );

  if (action === WEEK) element = element.replace('-', 'W');

  let hash = getHash(URL.eventParams);
  let url;
  if (action === DISPATCH || action === RESOURCE) {
    url = `${URL.org}/${URL.model}/${action.length ? action : URL.action}/`;
  } else {
    url = getURL({ ...URL, element }, filterForm, savedFilter, action);
  }

  if (reportType) {
    url = `
      ${URL.org}/
      ${URL.model}/
      ${action}/
    `.replace(/\s|\n|\t/g, '');
    const formatedToday = formatDate(getTimestamp(new Date()), 0, 'Y-m-d');
    hash = hash.length
      ? `${hash}&start=${formatedToday}&finish=${formatedToday}&pilot=${pilotID}`
      : `#?start=${formatedToday}&finish=${formatedToday}&pilot=${pilotID}`;
  }
  return `${url}${hash}`;
}

export function redirectWithParams(URL, filterForm, savedFilter) {
  window.location = getURL(URL, filterForm, savedFilter);
  return true;
}

export function handleCloseModal(props) {
  const {
    URL: { eventBreadcrumbs },
    paramsToHash,
    scrollY,
    switchModals
  } = props;
  const previousIndex = eventBreadcrumbs.length - 2;

  if (previousIndex > -1) {
    const nextBreadcrumbs = eventBreadcrumbs.slice(0, -1);
    return switchModals(nextBreadcrumbs[previousIndex].action, undefined, nextBreadcrumbs);
  }

  paramsToHash({}, scrollY);
}

export function getFilterTogglerState(filterName, URL, userFilters) {
  const userFilter = URL.params.filter && userFilters[URL.params.filter];

  const filterData = (userFilter && userFilter.filter) || {};

  return URL.params[filterName] || filterData[filterName] || defaultFilterForm[filterName] || '0';
}

export function getEventStyles(event, activityTypes) {
  if (event.type === 'maintenance') {
    return;
  }

  const backgroundColor = (activityTypes[event.activityType] || {}).color || DEFAULT_EVENT_COLOR;

  return {
    backgroundColor,
    color: getContrastColor(backgroundColor)
  };
}
